import React from 'react';
import './NotificationBanner.css';

export type BannerVariant = 'info' | 'success' | 'warning' | 'error';

export interface NotificationBannerProps {
  variant?: BannerVariant;
  title?: string;
  message: string;
  details?: string[];
  actions?: Array<{
    label: string;
    onClick: () => void;
    variant?: 'primary' | 'secondary';
  }>;
  onDismiss?: () => void;
  icon?: string;
}

/**
 * NotificationBanner - Persistent notification that appears below the header
 * Used for important messages that require user attention or action
 *
 * Based on modern UX patterns from:
 * - Carbon Design System
 * - VSCode UX Guidelines
 * - Material Design
 */
export function NotificationBanner({
  variant = 'info',
  title,
  message,
  details,
  actions,
  onDismiss,
  icon
}: NotificationBannerProps) {
  const defaultIcons: Record<BannerVariant, string> = {
    info: 'ℹ️',
    success: '✅',
    warning: '⚠️',
    error: '❌'
  };

  const displayIcon = icon || defaultIcons[variant];

  return (
    <div className={`notification-banner notification-banner--${variant}`} role="alert">
      <div className="notification-banner__icon">
        {displayIcon}
      </div>

      <div className="notification-banner__content">
        {title && <div className="notification-banner__title">{title}</div>}
        <div className="notification-banner__message">{message}</div>

        {details && details.length > 0 && (
          <ul className="notification-banner__details">
            {details.map((detail, index) => (
              <li key={index}>{detail}</li>
            ))}
          </ul>
        )}
      </div>

      {actions && actions.length > 0 && (
        <div className="notification-banner__actions">
          {actions.map((action, index) => (
            <button
              key={index}
              className={`notification-banner__action notification-banner__action--${action.variant || 'secondary'}`}
              onClick={action.onClick}
            >
              {action.label}
            </button>
          ))}
        </div>
      )}

      {onDismiss && (
        <button
          className="notification-banner__dismiss"
          onClick={onDismiss}
          aria-label="Dismiss notification"
        >
          ✕
        </button>
      )}
    </div>
  );
}
